import mitt from 'mitt'
import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { postVerifyTwoFactorAuth, signInUsernamePassword } from '../api/sign-in'
import { TooManyRequestsError } from '../api/TooManyRequestsError'
import { UnauthorizedError } from '../api/UnauthorizedError'
import { useUserStore } from '../stores/user'

export const EVENT_VERIFICATION_FAILED = 'verification_failed'

export const useSigninPageStore = defineStore('signin_page', () => {
  const userStore = useUserStore()

  const signingIn = ref(false)
  const verifyingCode = ref(false)
  const verificationError = ref(null)
  const error = ref(null)
  const form = ref({
    email: '',
    password: '',
    verificationCode: ''
  })

  const emitter = mitt()

  const canVerifyCode = computed(
    () => form.value.verificationCode.length === 6 && !verifyingCode.value
  )

  function setEmail(email) {
    form.value.email = email
  }

  function setPassword(password) {
    form.value.password = password
  }

  function setVerificationCode(code) {
    form.value.verificationCode = code
  }

  function signInWithUsernamePassword() {
    signingIn.value = true
    error.value = null

    return signInUsernamePassword(form.value.email, form.value.password)
      .then(data => {
        userStore.setUser(
          data.email,
          data.display_name,
          data.customers,
          data.is_admin,
          data.can_view_components,
          data.last_session,
          data.two_factor_auth_set_up,
          data.two_factor_auth_verified
        )

        form.value.email = ''
        form.value.password = ''
      })
      .catch(e => {
        if (e instanceof UnauthorizedError) {
          error.value = 'sign_in.error.unauthorized'
        } else if (e instanceof TooManyRequestsError) {
          error.value = 'sign_in.error.too_many_failed_logins'
        } else {
          error.value = 'sign_in.error.unknown_error'
        }
      })
      .finally(() => {
        signingIn.value = false
      })
  }

  function verifyCode() {
    if (!canVerifyCode.value) {
      return
    }

    verifyingCode.value = true
    verificationError.value = false

    postVerifyTwoFactorAuth(form.value.verificationCode)
      .then(async data => {
        // set veryfingCode to false in then/catch instead of in finally, otherwise the input field is disabled and cannot be focussed
        verifyingCode.value = false

        if (!data.code_verified) {
          verificationError.value = 'sign_in.error.incorrect_verification_code'

          nextTick(() => {
            emitter.emit(EVENT_VERIFICATION_FAILED)
          })
        } else {
          form.value.verificationCode = ''
          await userStore.fetchUser()
        }
      })
      .catch(e => {
        verifyingCode.value = false
        verificationError.value =
          e instanceof TooManyRequestsError
            ? 'sign_in.error.too_many_failed_verifications'
            : 'sign_in.error.unknown_error'
      })
  }

  function cancelVerification() {
    userStore.unsetUser()
  }

  function addEventListener(type, handler) {
    emitter.on(type, handler)
  }

  function removeEventListener(type, handler) {
    emitter.off(type, handler)
  }

  return {
    form,
    signingIn,
    verifyingCode,
    canVerifyCode,
    verificationError,
    error,
    setEmail,
    setPassword,
    setVerificationCode,
    signInWithUsernamePassword,
    verifyCode,
    cancelVerification,
    addEventListener,
    removeEventListener
  }
})
